import { Alert, AlertTitle, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { useController } from '../../hooks/useController'
import { useTexts } from '../../useTexts'

export function ConstraintAlert() {
  const texts = useTexts()
  const {
    data: { constraintScenario, affectedVehiclesCount, ignoreConstraints, hasExceptionConflict },
    updateData,
  } = useController()

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateData({ ignoreConstraints: event.target.checked })
    },
    [updateData],
  )

  if (constraintScenario === 'none') return null

  // The alert switches to the info variant purely based on the ignore checkbox.
  const severity = ignoreConstraints ? 'info' : 'warning'

  const title = (() => {
    switch (constraintScenario) {
      case 'minHours':
        return texts.alertMinHoursTitle
      case 'minLoad':
        return texts.alertMinLoadTitle
      case 'both':
        return texts.alertBothTitle
    }
  })()

  const bodyId = (() => {
    switch (constraintScenario) {
      case 'minHours':
        return hasExceptionConflict
          ? texts.alertMinHoursBodyWithExceptions
          : texts.alertMinHoursBody
      case 'minLoad':
        return hasExceptionConflict ? texts.alertMinLoadBodyWithExceptions : texts.alertMinLoadBody
      case 'both':
        return hasExceptionConflict ? texts.alertBothBodyWithExceptions : texts.alertBothBody
    }
  })()

  const checkboxLabel = (() => {
    switch (constraintScenario) {
      case 'minHours':
        return texts.alertMinHoursCheckbox
      case 'minLoad':
        return texts.alertMinLoadCheckbox
      case 'both':
        return texts.alertBothCheckbox
    }
  })()

  return (
    <Alert
      severity={severity}
      sx={{
        alignItems: 'flex-start',
        // Info state uses tidal skyBlue/800 for all text (title, body, checkbox)
        ...(severity === 'info' && { color: '#22548C' }),
      }}
    >
      <AlertTitle sx={{ mb: 0.5, fontSize: '1rem', fontWeight: 500 }}>{title}</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <FormattedMessage
          id={bodyId}
          values={{
            count: affectedVehiclesCount,
            b: chunks => (
              <Typography component="span" variant="subtitle2" display="inline">
                {chunks}
              </Typography>
            ),
          }}
        />
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="info"
            checked={ignoreConstraints}
            onChange={handleCheckboxChange}
            data-trackid="route-optimization__ignore-constraints"
            data-testid="route-optimization__ignore-constraints"
            sx={{ py: 0 }}
          />
        }
        label={
          <Typography variant="body2" color="inherit">
            {checkboxLabel}
          </Typography>
        }
      />
    </Alert>
  )
}
